import { useState, useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { HotelFormData } from './ManageHotelForm';
import PackageIcon from '../../assets/Package.svg';
import HotelsIcon from '../../assets/hotels.svg';
import FlightsandHotelIcon from '../../assets/Flights-and-Hotels.svg';
// import { useSearchContext } from '../../contexts/SearchContext';
// import { searchSerpHotels } from '../../api-client';
import CountryDropdown from './CountryDropdown';
import { fetchPropertyDetails } from '../../api-client';
import { Property } from '../../../../backend/src/models/getProperty';
import { PropertyDetails } from '../../../../backend/src/models/getPropertyDetails';

interface DetailsSectionProps {
  isEditMode: boolean;
}

const DetailsSection: React.FC<DetailsSectionProps> = ({ isEditMode }) => {
  const { register, setValue, getValues, formState: { errors }, control } = useFormContext<HotelFormData>();
  const [packageType] = useState<string>('');
  const [cancellationType] = useState<string>('');
  const [selectedHotel, setSelectedHotel] = useState<Property | null>(null);
  const [selectedHotelDetails, setSelectedHotelDetails] = useState<PropertyDetails | null>(null);
  

  const { fields: faqFields, append: addFaqField, remove: removeFaqField } = useFieldArray({
    control,
    name: 'faqQuestions',
  });

  const { fields: faqAnsFields, append: addFaqAnsField, remove: removeFaqAnsField } = useFieldArray({
    control,
    name: 'faqAnswers',
  });

  const { fields: dealHighlightFields, append: adddealHighlightField, remove: removedealHighlightField } = useFieldArray({
    control,
    name: 'dealHighlights',
  });

  useEffect(() => {
    setValue('packageType', packageType);
    setValue('cancellationType', cancellationType);
  }, [packageType, cancellationType, setValue]);

  const renderSVGIcon = () => {
    const packageTypeValue = getValues("packageType");
    switch (packageTypeValue) {
      case './src/assets/Package.svg':
        return <img loading="lazy" src={PackageIcon} alt="Hotels Only" />;
      case './src/assets/hotels.svg':
        return <img loading="lazy" src={HotelsIcon} alt="Hotels Only" />;
      case './src/assets/Flights-and-Hotels.svg':
        return <img loading="lazy" src={FlightsandHotelIcon} alt="Flights and Hotels Only" />;
      default:
        return null;
    }
  };  

  useEffect(() => {
    if (selectedHotel) {
      setValue('name', selectedHotel.name);

      const countryString = selectedHotel.locationText?.toString() || '';
      const country = countryString.split(',').pop()?.trim() || '';
      setValue('country', country);
      setValue('destId', selectedHotel.code.toString());

      if (selectedHotelDetails) {
        setValue('city', selectedHotelDetails.cityName);
        setValue('longitude', selectedHotelDetails.longitude.toString());
        setValue('latitude', selectedHotelDetails.latitude.toString());
        setValue('description', selectedHotelDetails.description ?? '');
        const inputString = selectedHotelDetails.rating_label;
        const number = parseInt(inputString.match(/\d+/)?.[0] || '', 10);
        setValue('starRating', number);
      }
    }
  }, [selectedHotel, selectedHotelDetails, setValue]);


  const handleHotelSelect = async (hotel: Property, destinationCode: string) => {
    // console.log('Selected hotel:', hotel);
    setSelectedHotel(hotel);
    setValue('destinationCode', destinationCode);
    try {
      const hotelDetailsData = await fetchPropertyDetails(destinationCode, hotel.code);
      // console.log('Selected hotel details:', hotelDetailsData);
      setSelectedHotelDetails(hotelDetailsData);
    } catch (error) {
      console.error('Error fetching hotel details:', error);
    }
  };
  
  

  return (
    <div className="flex flex-col gap-8 mt-10 max-w-full mx-auto p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-3xl font-bold text-center uppercase">
        {isEditMode ? 'Edit Hotel' : 'Add Hotel'}
      </h1>
      <hr className="border-gray-200 w-10/12 mx-auto" />
      <CountryDropdown onHotelSelect={handleHotelSelect}/>

      <div className="flex flex-wrap gap-4 justify-between">
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px] max-w-[50%]">
          Meta Title
          <input
            type="text"
            className="border rounded w-full p-2 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("metaTitle", { required: "This field is required" })}
          />
          {errors.metaTitle && (
            <span className="text-red-500">{errors.metaTitle.message}</span>
          )}
        </label>

        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px] max-w-[50%]">
          Meta Description
          <input
            type="text"
            className="border rounded w-full p-2 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("metaDescription", { required: "This field is required" })}
          />
          {errors.metaDescription && (
            <span className="text-red-500">{errors.metaDescription.message}</span>
          )}
        </label>
      </div>
      
      <input type='hidden'
        {...register("destinationCode", {required: ''})}
      />

      <div className="flex flex-wrap gap-4 justify-center">
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px]">
          City
          <input
            type="text"
            className="border rounded w-full py-2 px-3 mt-1 font-normal focus:outline-none focus:border-pink-400 capitalize"
            {...register("city", { required: "This field is required" })}
          />
          {errors.city && <span className="text-red-500">{errors.city.message}</span>}
        </label>
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px]">
          Country
          <input
            type="text"
            className="border rounded w-full py-2 px-3 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("country", { required: "This field is required" })}
          />
          {errors.country && <span className="text-red-500">{errors.country.message}</span>}
        </label>
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px]">
          Latitude
          <input
            type="text"
            className="border rounded w-full py-2 px-3 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("latitude", { required: "This field is required" })}
          />
          {errors.latitude && <span className="text-red-500">{errors.latitude.message}</span>}
        </label>
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px]">
          Longitude
          <input
            type="text"
            className="border rounded w-full py-2 px-3 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("longitude", { required: "This field is required" })}
          />
          {errors.longitude && <span className="text-red-500">{errors.longitude.message}</span>}
        </label>
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px]">
          Trip advisor reviews
          <input
            type="text"
            className="border rounded w-full py-2 px-3 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("tripAdvisorRating", { required: "This field is required" })}
          />
          {errors.tripAdvisorRating && <span className="text-red-500">{errors.tripAdvisorRating.message}</span>}
        </label>
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px]">
          Trip advisor rating
          <input
            type="text"
            className="border rounded w-full py-2 px-3 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("tripRating", { required: "This field is required" })}
          />
          {errors.tripRating && <span className="text-red-500">{errors.tripRating.message}</span>}
        </label>
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px]">
          Hotel ID
          <input
            type="text"
            className="border rounded w-full py-2 px-3 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("destId", { required: "This field is required" })}
          />
          {errors.destId && <span className="text-red-500">{errors.destId.message}</span>}
        </label>
        {/* <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px]">
          Serp ID
          <input
            type="text"
            className="border rounded w-full py-2 px-3 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("serpId", { required: "This field is required" })}
          />
          {errors.serpId && <span className="text-red-500">{errors.serpId.message}</span>}
        </label> */}
      </div>

      <label className="text-gray-700 text-sm font-bold flex-1">
        Description
        <textarea
          className="border rounded w-full py-1 px-2 font-normal mb-2"
          {...register("description", { required: "This field is required" })}
          placeholder="Generated description will appear here..."
        ></textarea>
        {/* <button
          onClick={fetchDescription}
          className="px-4 py-2.5 border text-blue-600 text-[13px] rounded uppercase border-blue-600 hover:bg-blue-600 hover:text-white transition-colors duration-200"
          disabled={isLoadingDescription}
          aria-label="Generate Description"
        >
          {isLoadingDescription ? 'Loading...' : 'Generate Description'}
        </button> */}
      </label>

      <div className="flex flex-wrap gap-4 justify-center">
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px] max-w-[33%]">
          Price Per Night
          <input
            type="number"
            min={1}
            className="border rounded w-full p-2 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("pricePerNight", { required: "This field is required" })}
          />
          {errors.pricePerNight && (
            <span className="text-red-500">{errors.pricePerNight.message}</span>
          )}
        </label>
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px] max-w-[33%]">
          Deposit Price
          <input
            type="number"
            min={1}
            className="border rounded w-full p-2 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("depositPrice", { required: "This field is required" })}
          />
          {errors.depositPrice && (
            <span className="text-red-500">{errors.depositPrice.message}</span>
          )}
        </label>
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px] max-w-[33%]">
          Star Rating
          <select
            {...register("starRating", {
              required: "This field is required",
            })}
            className="border rounded w-full p-2 mt-1 text-gray-700 font-normal focus:outline-none focus:border-pink-400"
          >
            <option value="" className="text-sm font-bold">
              Select a Rating
            </option>
            {[1, 2, 3, 4, 5].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
          {errors.starRating && (
            <span className="text-red-500">{errors.starRating.message}</span>
          )}
        </label>
      </div>

      <div className="flex flex-wrap gap-4 justify-between">
        {/* <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px] max-w-[50%]">
          Rooms and Board Basis
          <input
            type="text"
            className="border rounded w-full p-2 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("roomsAndBoard", { required: "This field is required" })}
          />
          {errors.roomsAndBoard && (
            <span className="text-red-500">{errors.roomsAndBoard.message}</span>
          )}
        </label>

        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px] max-w-[50%]">
          Cancellation Policy
          <input
            type="text"
            className="border rounded w-full p-2 mt-1 font-normal focus:outline-none focus:border-pink-400"
            {...register("cancellation", { required: "This field is required" })}
          />
          {errors.cancellation && (
            <span className="text-red-500">{errors.cancellation.message}</span>)}
        </label> */}
        
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px] max-w-[50%] flex items-center">
          Show Hotel
          <input
            type="checkbox"
            className="ml-2 transform scale-125"
            {...register("showHotel")}
            defaultChecked={true} // Default to false
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-4 justify-between">
        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px] max-w-[33%]">
          Package Type
          <div className="flex items-center mt-1">
            <select
              {...register("packageType", {
                required: "This field is required",
              })}
              className="border rounded p-2 text-gray-700 font-normal mr-2 w-6/12 focus:outline-none focus:border-pink-400"
            >
              <option value="">Select a Package</option>
              <option value="./src/assets/Package.svg">Flights and Hotels and Baggage</option>
              <option value="./src/assets/hotels.svg">Hotels only</option>
              <option value="./src/assets/Flights-and-Hotels.svg">Flights and Hotels only</option>
            </select>
            <div className="ml-2 w-40">
              {renderSVGIcon()}
            </div>
          </div>
          {errors.packageType && (
            <span className="text-red-500">{errors.packageType.message}</span>
          )}
        </label>

        {/* <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px] max-w-[33%]">
          Cancellation Type
          <div className="flex items-center mt-1">
            <select
              {...register("cancellationType", {
                required: "This field is required",
              })}
              className="border rounded p-2 text-gray-700 font-normal mr-2 w-6/12 focus:outline-none focus:border-pink-400"
            >
              <option value="">Select a Policy</option>
              <option value="Free Cancellation">Free Cancellation</option>
              <option value="Non Refundable">Non Refundable</option>
            </select>
            <div className="ml-2 w-50">
              {cancellationPolicy()}
            </div>
          </div>
          {errors.cancellationType && (
            <span className="text-red-500">{errors.cancellationType.message}</span>
          )}
        </label> */}

        <label className="text-gray-700 text-sm font-bold flex-1 min-w-[200px] max-w-[33%]">
          Availability [Only for Flash Deals]
          <div className="flex items-center mt-1">
            <select
              {...register("availability", { required: "This field is required" })}
              className="border rounded p-2 text-gray-700 font-normal mr-2 w-6/12 focus:outline-none focus:border-pink-400"
            >
              <option value="">Select Availability</option>
              <option value="Available">Available</option>
              <option value="Sold Out">Sold Out</option>
            </select>
          </div>
          {errors.availability && (
            <span className="text-red-500">{errors.availability.message}</span>
          )}
        </label>
      </div>

      {/* <label className="text-gray-700 text-sm font-bold">
        What's exciting?
        {excitingFields.map((field, index) => (
          <div key={field.id} className="flex items-center mb-2">
            <input
              type="text"
              className="border rounded w-full py-2 px-3 mt-1 text-gray-700 focus:outline-none focus:border-pink-400"
              {...register(`whatsExciting.${index}.value`, { required: "This field is required" })}
              defaultValue={field.value || ''}
            />
            <button
              type="button"
              className="px-3 py-2 border text-red-600 text-sm rounded uppercase border-red-600 hover:bg-red-600 hover:text-white transition-colors duration-200 ml-2 mt-1"
              onClick={() => removeExcitingField(index)}
            >
              Remove
            </button>
          </div>
        ))}
        {excitingFields.length < 3 && (
          <button
            type="button"
            className="px-4 py-2 border text-green-600 text-sm rounded uppercase border-green-600 hover:bg-green-600 hover:text-white transition-colors duration-200 ml-1"
            onClick={() => addExcitingField({ value: '' })}
          >
            Add Field
          </button>
        )}
      </label> */}

      <label className="text-gray-700 text-sm font-bold mt-4">
        Deal Highlights
        {dealHighlightFields.map((field, index) => (
          <div key={field.id} className="flex items-center mb-2">
            <input
              type="text"
              className="border rounded w-full py-2 px-3 mt-1 text-gray-700 focus:outline-none focus:border-pink-400"
              {...register(`dealHighlights.${index}.value`, { required: "This field is required" })}
              defaultValue={field.value}
            />
            <button
              type="button"
              className="px-3 py-2 border text-red-600 text-sm rounded uppercase border-red-600 hover:bg-red-600 hover:text-white transition-colors duration-200 ml-2 mt-1"
              onClick={() => removedealHighlightField(index)}
            >
              Remove
            </button>
          </div>
        ))}
        {dealHighlightFields.length < 5 && (
          <button
            type="button"
            className="px-4 py-2 border text-green-600 text-sm rounded uppercase border-green-600 hover:bg-green-600 hover:text-white transition-colors duration-200 ml-1"
            onClick={() => adddealHighlightField({ value: '' })}
          >
            Add Field
          </button>
        )}
      </label>

      <label className="text-gray-700 text-sm font-bold mt-4">
        FAQ Questions
        {faqFields.map((field, index) => (
          <div key={field.id} className="flex items-center mb-2">
            <input
              type="text"
              className="border rounded w-full py-2 px-3 mt-1 text-gray-700 focus:outline-none focus:border-pink-400"
              {...register(`faqQuestions.${index}.value`, { required: "This field is required" })}
              defaultValue={field.value}
            />
            <button
              type="button"
              className="px-3 py-2 border text-red-600 text-sm rounded uppercase border-red-600 hover:bg-red-600 hover:text-white transition-colors duration-200 ml-2 mt-1"
              onClick={() => removeFaqField(index)}
            >
              Remove
            </button>
          </div>
        ))}
        {faqFields.length < 6 && (
          <button
            type="button"
            className="px-4 py-2 border text-green-600 text-sm rounded uppercase border-green-600 hover:bg-green-600 hover:text-white transition-colors duration-200 ml-1"
            onClick={() => addFaqField({ value: '' })}
          >
            Add Field
          </button>
        )}
      </label>

      <label className="text-gray-700 text-sm font-bold mt-4">
        FAQ Answers
        {faqAnsFields.map((field, index) => (
          <div key={field.id} className="flex items-center mb-2">
            <input
              type="text"
              className="border rounded w-full py-2 px-3 mt-1 text-gray-700 focus:outline-none focus:border-pink-400"
              {...register(`faqAnswers.${index}.value`, { required: "This field is required" })}
              defaultValue={field.value}
            />
            <button
              type="button"
              className="px-3 py-2 border text-red-600 text-sm rounded uppercase border-red-600 hover:bg-red-600 hover:text-white transition-colors duration-200 ml-2 mt-1"
              onClick={() => removeFaqAnsField(index)}
            >
              Remove
            </button>
          </div>
        ))}
        {faqAnsFields.length < 6 && (
          <button
            type="button"
            className="px-4 py-2 border text-green-600 text-sm rounded uppercase border-green-600 hover:bg-green-600 hover:text-white transition-colors duration-200 ml-1"
            onClick={() => addFaqAnsField({ value: '' })}
          >
            Add Field
          </button>
        )}
      </label>
    </div>
  );
};

export default DetailsSection;
